/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import PolicyLanguageToggle, {
  usePolicyLanguage,
} from "../components/PolicyLanguageToggle";

const CONTACT_EMAIL = "lcbeltran92@gmail.com";

const contactReasons = [
  { id: "general", es: "Pregunta general", en: "General question" },
  { id: "support", es: "Soporte técnico", en: "Technical support" },
  {
    id: "deletion",
    es: "Solicitud de eliminación de cuenta",
    en: "Account deletion request",
  },
  { id: "privacy", es: "Solicitud de privacidad", en: "Privacy request" },
  {
    id: "report",
    es: "Denunciar contenido o a un usuario",
    en: "Report content or a user",
  },
  {
    id: "copyright",
    es: "Derechos de autor o notificación legal",
    en: "Copyright or legal notice",
  },
  {
    id: "feedback",
    es: "Comentario o sugerencia",
    en: "Feedback or suggestion",
  },
];

const copy = {
  es: {
    title: "Contactar a BioIndex",
    introduction:
      "Utiliza esta página para preguntas, soporte técnico, comentarios, denuncias, asuntos de privacidad, notificaciones legales o solicitudes de eliminación de cuentas. Completa el formulario y BioIndex preparará un correo electrónico en la aplicación de correo de tu dispositivo.",
    name: "Nombre",
    email: "Tu correo electrónico",
    reason: "Motivo de contacto",
    subject: "Asunto",
    subjectPlaceholder: "Describe brevemente tu solicitud",
    message: "Mensaje",
    messagePlaceholder: "¿Cómo podemos ayudarte?",
    prepare: "Preparar correo",
    submitNote:
      "Tu mensaje no se envía automáticamente. El botón abre tu aplicación de correo para que puedas revisarlo y enviarlo.",
    directTitle: "Correo directo",
    directText:
      "Si el formulario no abre tu aplicación de correo, escríbenos a:",
    deletionTitle: "Eliminación de cuentas",
    deletionText:
      "Selecciona “Solicitud de eliminación de cuenta” e identifica la dirección de correo asociada con tu cuenta de BioIndex. BioIndex puede contactarte para verificar que eres el titular antes de completar la solicitud.",
    returnHome: "Volver a BioIndex",
    rights: "Todos los derechos reservados.",
    emailName: "Nombre",
    emailReply: "Correo de respuesta",
    emailReason: "Motivo",
  },
  en: {
    title: "Contact BioIndex",
    introduction:
      "Use this page for questions, technical support, feedback, reports, privacy matters, legal notices, or account-deletion requests. Complete the form below and BioIndex will prepare an email in your device’s email application.",
    name: "Name",
    email: "Your email",
    reason: "Reason for contacting us",
    subject: "Subject",
    subjectPlaceholder: "Briefly describe your request",
    message: "Message",
    messagePlaceholder: "How can we help?",
    prepare: "Prepare Email",
    submitNote:
      "Your message is not sent automatically. The button opens your email application so you can review and send it.",
    directTitle: "Email directly",
    directText:
      "If the form does not open your email application, write to:",
    deletionTitle: "Account deletion",
    deletionText:
      "Select “Account deletion request” and identify the email address associated with your BioIndex account. BioIndex may contact you to verify ownership before completing the request.",
    returnHome: "Return to BioIndex",
    rights: "All rights reserved.",
    emailName: "Name",
    emailReply: "Reply email",
    emailReason: "Reason",
  },
};

export default function ContactPage() {
  const [language, setLanguage] = usePolicyLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState(contactReasons[0].id);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const content = copy[language];

  const handleSubmit = (event) => {
    event.preventDefault();

    const selectedReason =
      contactReasons.find((contactReason) => contactReason.id === reason) ||
      contactReasons[0];
    const reasonLabel = selectedReason[language];
    const emailSubject = `[BioIndex] ${reasonLabel}${
      subject.trim() ? `: ${subject.trim()}` : ""
    }`;

    const emailBody = [
      `${content.emailName}: ${name.trim()}`,
      `${content.emailReply}: ${email.trim()}`,
      `${content.emailReason}: ${reasonLabel}`,
      "",
      message.trim(),
    ].join("\n");

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      emailSubject,
    )}&body=${encodeURIComponent(emailBody)}`;
  };

  const fieldClassName =
    "mt-2 w-full rounded-xl border border-[#374049]/15 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition placeholder:text-[#374049]/35 focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15";

  return (
    <main className="min-h-screen bg-[#374049] px-4 py-8 text-[#374049]">
      <section className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FCFFF6] shadow-lg">
        <div className="bg-[#374049] px-6 py-4">
          <img
            src="/BioIndex Prototype Banner.png"
            alt="BioIndex"
            className="mx-auto max-h-40 w-full max-w-md object-contain"
          />
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex justify-end">
            <PolicyLanguageToggle
              language={language}
              onChange={setLanguage}
            />
          </div>

          <div className="mt-6 border-b border-[#374049]/10 pb-6">
            <h1
              className="text-4xl font-semibold"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {content.title}
            </h1>

            <p className="mt-5 max-w-3xl text-sm leading-7 text-[#374049]/75">
              {content.introduction}
            </p>
          </div>

          <div className="mt-6 grid gap-6 md:grid-cols-[minmax(0,1fr)_260px]">
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl border border-[#374049]/10 bg-white/70 p-5 shadow-sm sm:p-6"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="text-sm font-semibold text-[#374049]">
                  {content.name}
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className={fieldClassName}
                    autoComplete="name"
                    required
                  />
                </label>

                <label className="text-sm font-semibold text-[#374049]">
                  {content.email}
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className={fieldClassName}
                    autoComplete="email"
                    required
                  />
                </label>
              </div>

              <label className="mt-5 block text-sm font-semibold text-[#374049]">
                {content.reason}
                <select
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className={fieldClassName}
                >
                  {contactReasons.map((contactReason) => (
                    <option key={contactReason.id} value={contactReason.id}>
                      {contactReason[language]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="mt-5 block text-sm font-semibold text-[#374049]">
                {content.subject}
                <input
                  type="text"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  className={fieldClassName}
                  placeholder={content.subjectPlaceholder}
                  required
                />
              </label>

              <label className="mt-5 block text-sm font-semibold text-[#374049]">
                {content.message}
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  className={`${fieldClassName} min-h-40 resize-y`}
                  placeholder={content.messagePlaceholder}
                  required
                />
              </label>

              <button
                type="submit"
                className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[#24C3BE] px-6 py-3 text-sm font-semibold text-[#374049] transition hover:bg-[#20b2ae] focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/40 focus:ring-offset-2 sm:w-auto"
              >
                {content.prepare}
              </button>

              <p className="mt-4 text-xs leading-6 text-[#374049]/55">
                {content.submitNote}
              </p>
            </form>

            <aside className="space-y-4">
              <div className="rounded-2xl border border-[#374049]/10 bg-white/70 p-5 shadow-sm">
                <h2 className="text-base font-semibold text-[#374049]">
                  {content.directTitle}
                </h2>
                <p className="mt-3 text-sm leading-7 text-[#374049]/70">
                  {content.directText}
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="mt-2 block break-all text-sm font-medium text-[#01767A] underline"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>

              <div className="rounded-2xl border border-[#93CA87]/40 bg-[#93CA87]/10 p-5">
                <h2 className="text-base font-semibold text-[#374049]">
                  {content.deletionTitle}
                </h2>
                <p className="mt-3 text-sm leading-7 text-[#374049]/70">
                  {content.deletionText}
                </p>
              </div>
            </aside>
          </div>

          <div className="mt-8 flex flex-col items-center border-t border-[#374049]/10 pt-7 text-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl bg-[#374049] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2f373e]"
            >
              {content.returnHome}
            </Link>

            <p className="mt-5 text-xs leading-6 text-[#374049]/55">
              © {new Date().getFullYear()} BioIndex™. {content.rights}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
